import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Paper, Grid } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const scoreToQualitativeMap = {
  1: "5 - Bastante alejado del esperado",
  2: "4 - Desempeño por debajo del esperado",
  3: "3 - Desempeño esperado",
  4: "2 - Desempeño por encima del esperado",
  5: "1 - Excede ampliamente las expectativas",
};

export default function ComparisonByPeriod({ allTrendsData, periodOptions }) {
  const [selectedPeriod1, setSelectedPeriod1] = useState('');
  const [selectedPeriod2, setSelectedPeriod2] = useState('');

  const handlePeriod1Change = (event) => {
    setSelectedPeriod1(event.target.value);
  };

  const handlePeriod2Change = (event) => {
    setSelectedPeriod2(event.target.value);
  };

  const distributions = useMemo(() => {
    const dist1 = selectedPeriod1 ? allTrendsData.find(d => d.periodo === selectedPeriod1)?.metricas_generales?.distribucion_puntaje : null;
    const dist2 = selectedPeriod2 ? allTrendsData.find(d => d.periodo === selectedPeriod2)?.metricas_generales?.distribucion_puntaje : null;
    return { dist1, dist2 };
  }, [allTrendsData, selectedPeriod1, selectedPeriod2]);

  const labels = useMemo(() => Object.keys(scoreToQualitativeMap).sort((a, b) => parseInt(a) - parseInt(b)), []);

  const data = useMemo(() => {
    const datasets = [];

    if (distributions.dist1) {
      const totalScores1 = Object.values(distributions.dist1).reduce((sum, count) => sum + count, 0);
      const datasetData1 = labels.map(label => totalScores1 > 0 ? (distributions.dist1[parseInt(label)] || 0) / totalScores1 : 0);
      datasets.push({
        label: `Distribución: ${selectedPeriod1}`,
        data: datasetData1,
        backgroundColor: 'rgba(53, 162, 235, 0.2)',
        borderColor: 'rgba(53, 162, 235, 1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
      });
    }

    if (distributions.dist2) {
      const totalScores2 = Object.values(distributions.dist2).reduce((sum, count) => sum + count, 0);
      const datasetData2 = labels.map(label => totalScores2 > 0 ? (distributions.dist2[parseInt(label)] || 0) / totalScores2 : 0);
      datasets.push({
        label: `Distribución: ${selectedPeriod2}`,
        data: datasetData2,
        backgroundColor: 'rgba(255, 159, 64, 0.2)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
      });
    }

    return {
      labels: labels.map(label => label),
      datasets,
    };
  }, [labels, distributions, selectedPeriod1, selectedPeriod2]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Comparativa: Puntaje Calibrado vs. Original (Distribución)',
        font: { size: 18, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const numericScore = context.label;
            const fullScoreLabel = scoreToQualitativeMap[parseInt(numericScore)];
            const rawCount = context.dataset.label.includes(selectedPeriod1) ? distributions.dist1[parseInt(numericScore)] : distributions.dist2[parseInt(numericScore)];
            return `${label}: ${fullScoreLabel} (Total: ${rawCount || 0})`;
          }
        },
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Puntaje',
          font: { size: 14, weight: 'bold' },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Proporción de Evaluaciones',
          font: { size: 14, weight: 'bold' },
        },
        beginAtZero: true,
      },
    },
  }), [distributions, selectedPeriod1, selectedPeriod2]);

  if (periodOptions.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', textAlign: 'center' }}>
        <Typography variant="subtitle1">No hay períodos disponibles para seleccionar.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: '12px' }}>
      <Box mb={2} display="flex" gap={2}>
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Período 1</InputLabel>
          <Select
            value={selectedPeriod1}
            label="Período 1"
            onChange={handlePeriod1Change}
          >
            <MenuItem value="">Seleccione</MenuItem>
            {periodOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Período 2</InputLabel>
          <Select
            value={selectedPeriod2}
            label="Período 2"
            onChange={handlePeriod2Change}
          >
            <MenuItem value="">Seleccione</MenuItem>
            {periodOptions.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!selectedPeriod1 && !selectedPeriod2 && (
        <Typography variant="subtitle1" textAlign="center" mt={4}>Seleccione al menos un período para comparar.</Typography>
      )}

      {(selectedPeriod1 || selectedPeriod2) && (
        <Box sx={{ height: '500px', width: '100%' }}>
          <Line data={data} options={options} />
        </Box>
      )}
    </Paper>
  );
} 